import { useState, useEffect } from "react";
import { api, handleAuthFailure } from "../data/api";
import Badge from "../components/Badge";
import Pill from "../components/Pill";
import StatCard from "../components/StatCard";
import { TableWrap, TH, TD } from "../components/TableWrap";
import { useTheme } from "../context/ThemeContext";


export default function AffiliateDashboard({ user }) {
  const { mode } = useTheme();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await api.affiliates.my(user.affiliate_id);
      setDevices(rows);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError("Failed to load your devices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const online = devices.filter((d) => d.status === "online").length;
  const offline = devices.filter((d) => d.status === "offline" && d.device_type !== "self-srt-connect").length;

  return (
    <>
      <div
        style={{
          marginBottom: 18,
          paddingBottom: 14,
          borderBottom: `1px solid var(--border)`,
        }}
      >
        <h1 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>
          My Devices
        </h1>
        <div style={{ fontSize: 12, color: "var(--text-sub)", marginTop: 3 }}>
          {user.name || user.email} — {user.affiliate_id}
        </div>
      </div>

      {error && (
        <div
          style={{
            background: "var(--red-bg)",
            border: `1px solid var(--red-border)`,
            borderRadius: 7,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12,
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-dim)",
            padding: "40px 0",
            textAlign: "center",
          }}
        >
          Loading devices…
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            <StatCard label="Total Devices" value={devices.length} />
            <StatCard label="Online" value={online} />
            <StatCard label="Offline" value={offline} />
            <StatCard
              label="Channels"
              value={
                // Count every enabled channel, not one per device. `channel_id`
                // is MIN(dc.channel_id) — a backward-compat scalar — so on a
                // 6-channel RK3568 it reported 1. `channel_ids` holds them all.
                [
                  ...new Set(
                    devices.flatMap((d) =>
                      d.channel_ids?.length ? d.channel_ids : [d.channel_id]
                    ).filter(Boolean)
                  ),
                ].length
              }
            />
          </div>

          {/* Device table */}
          <TableWrap>
            <thead>
              <tr>
                <TH>Device ID</TH>
                <TH>Channel</TH>
                <TH>Channel Name</TH>
                <TH>Status</TH>
                <TH>IP Address</TH>
                <TH>Location / Model</TH>
                <TH>Last Seen</TH>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: "28px 14px",
                      textAlign: "center",
                      fontSize: 12,
                      color: "var(--text-dim)",
                    }}
                  >
                    No devices registered to your affiliate.
                  </td>
                </tr>
              ) : (
                devices.map((d) => (
                  <tr key={d.device_id}>
                    <TD mono style={{ fontWeight: 500 }}>
                      {d.device_id}
                    </TD>
                    <TD>
                      {(() => {
                        // Show every assigned channel. A multi-channel RK3568
                        // previously rendered only its lowest-sorted one.
                        const ids = d.channel_ids?.length
                          ? d.channel_ids
                          : (d.channel_id ? [d.channel_id] : []);
                        return ids.length ? (
                          <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
                            {ids.map((id) => <Pill key={id} label={id} />)}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
                            —
                          </span>
                        );
                      })()}
                    </TD>
                    <TD sub>
                      {/* /affiliates/my returns names only as MIN(display_name),
                          so name the first and count the rest. */}
                      {d.channel_name || "—"}
                      {d.channel_ids?.length > 1 && (
                        <span style={{ color: "var(--text-dim)" }}>
                          {" "}+{d.channel_ids.length - 1} more
                        </span>
                      )}
                    </TD>
                    <TD>
                      {/* Only online/offline for affiliate view — no degraded */}
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          background:
                            d.status === "online" ? "var(--green-bg)" : "var(--grey-bg)",
                          color: d.status === "online" ? "var(--green)" : "var(--grey)",
                          border: `1px solid ${d.status === "online" ? "var(--green-border)" : "var(--grey-border)"}`,
                          borderRadius: 4,
                          padding: "2px 7px",
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.03em",
                          textTransform: "uppercase",
                        }}
                      >
                        <span
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background:
                              d.status === "online" ? "var(--green)" : "var(--grey)",
                            flexShrink: 0,
                          }}
                        />
                        {d.status === "online" ? "Online" : "Offline"}
                      </span>
                    </TD>
                    <TD mono sub>
                      {d.ip_address || "—"}
                    </TD>
                    <TD sub>{d.device_type === "self-srt-connect" ? "" : (d.device_model || "—")}</TD>
                    {/* <TD sub>{d.last_seen ? new Date(d.last_seen).toLocaleString() : "—"}</TD> */}
                    <TD sub>
                      {d.last_seen
                        ? new Date(d.last_seen).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "Not yet seen"}
                    </TD>
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
        </>
      )}
    </>
  );
}
